import {
  FILLED_WASH_FILL,
  FORBIDDEN_FILL,
  FORBIDDEN_STROKE,
  GROUND_BASE_WIDTH,
  GROUND_HALO_OPACITY,
  REVERSE_WASH_FILL,
  SUBAREA_STROKE,
  SUBAREA_STROKE_OPACITY,
  groundEquivalentPrintZoom,
  groundShapes,
  groundStrokeMm,
  huntOutlineWidths,
  polygonCentroidOfRings,
  resolveHuntBorderColor,
  shouldDrawGroundHalo,
} from './mapBorders'
import type { GroundShape } from './mapBorders'
import { isLineKindVisible, type MapAppearance } from './mapAppearance'
import { filledWashPolygons, reverseWashPolygons } from './groundWash'
import {
  clipPolygonToRect,
  clipPolylineToRect,
  printPixelToleranceMetres,
  roundCoord,
  sheetRect,
  simplifyPolyline,
} from './printGeometry'
import type { Pt2, Rect } from './printGeometry'
import { LINE_BORDER_FACTOR, LINE_DEFAULT_COLOR } from './mapLines'
import { lineDashPattern } from './mapGeoJson'
import { lineStrokeMm, printArrowHead } from './printLineGeometry'
import { PAGE_PX_PER_MM } from './printLayout'
import { LEGACY_PRINT_LABEL_MM, printLineNameAnchor } from './printNameGeometry'
import { pinTypeVisibility } from './mapPinTypes'
import { ringsToPolygons } from './polygonUnion'
import { pageMm } from './printPaper'
import type { PaperFormat, PrintOrientation } from './printPaper'
import type { PrintProjector } from './printProjection'
import { DEFAULT_PRINT_MARGIN_MM, groundExtentMetres } from './printScale'
import { collectPrintSkips, printPinArtwork, type PrintOverlay, type PrintSkip } from './printSkips'
import type { GeoArea, GeoLine, GeoPt, TeamMap } from './types'

/**
 * Builds the print server's job description from a team's map.
 *
 * Two properties of the server's contract shape everything here. First,
 * validation is **strict**: an unknown or misspelled field rejects the job, so
 * this module emits exactly the documented keys and omits anything empty rather
 * than sending a null. Second, every size in the payload is **millimetres on
 * paper**, not pixels — so screen quantities cannot be forwarded, they have to
 * be re-expressed as cartographic ones. That is a design decision per feature
 * kind, and the constants below record each one.
 *
 * Colours need converting too: the canvas writes `#RRGGBBAA` (CSS), the server
 * reads `#AARRGGBB`. Getting that backwards yields a fully transparent fill or a
 * wildly wrong hue, with nothing to notice it but the finished PDF.
 */

// ── Print-specific sizing ─────────────────────────────────────────────────

/**
 * Printed height of a pin symbol in mm, at the default pin size — the print
 * panel's pin slider starts at this times the Pin size setting.
 *
 * 7.5 mm is the size in the server's own documentation example, and it holds the
 * on-screen proportion: a 47 px pin on a ~1200 px map is 3.9% of the width, and
 * 7.5 mm on A4's 190 mm printable width is the same 3.9%.
 *
 * That proportion is right for the screen and too heavy for a print, because the
 * two are composed differently — the map tab is zoomed into part of a ground
 * while a print is framed to cover all of it, so the same relative size puts far
 * more markers on the page. The user's Pin size setting is what resolves that,
 * which is why this is a baseline to scale rather than a fixed output size.
 */
export const PRINT_SYMBOL_HEIGHT_MM = 7.5

/**
 * Pin label font size in mm (the server's `label_height` is the em, not a cap
 * height), and its fill.
 *
 * Independent of the Pin size setting, matching both on-screen renderers — see
 * the note on `pinScale`. The print panel's label slider starts here.
 */
export const PRINT_LABEL_MM = LEGACY_PRINT_LABEL_MM
export const PRINT_LABEL_COLOR = '#ffffff'

/**
 * How large pins and their labels print, in millimetres on paper.
 *
 * Chosen per print in the print panel, because the right size depends on the
 * sheet: a whole ground at 1:50 000 wants small markers, a single drive at
 * 1:5 000 can carry large ones. A `labelMm` of 0 prints no labels.
 */
export interface PrintPinSizes {
  pinMm: number
  labelMm: number
}

/** The pin slider's travel, before `printPinSizeRange` widens it to fit a default. */
export const PRINT_PIN_MM_MIN = 3
export const PRINT_PIN_MM_MAX = 15
/** The label slider's travel; its 0 is how a print leaves the labels off. */
export const PRINT_LABEL_MM_MIN = 0
export const PRINT_LABEL_MM_MAX = 8

/**
 * The sizes a print starts at, before the user moves a slider: the pin follows
 * the map's Pin size setting, and the label follows the pin-names switch — off
 * means a 0 mm label, so a sheet composed with names hidden prints without them
 * until the user asks for them.
 */
export function defaultPrintPinSizes(appearance: MapAppearance): PrintPinSizes {
  return {
    pinMm: PRINT_SYMBOL_HEIGHT_MM * usablePinScale(appearance.pinScale),
    labelMm: appearance.pinNamesVisible ? PRINT_LABEL_MM : 0,
  }
}

/**
 * The pin slider's range: 3–15 mm, stretched to include `defaultMm` should the
 * Pin size setting ever put the starting value outside it — a slider that
 * cannot show the value it starts at would clamp it on first touch.
 */
export function printPinSizeRange(defaultMm: number): { min: number; max: number } {
  return {
    min: Math.min(PRINT_PIN_MM_MIN, defaultMm),
    max: Math.max(PRINT_PIN_MM_MAX, defaultMm),
  }
}

/**
 * The area colours come from the shared border law rather than being restated here.
 *
 * They were five local literals, which is how print and screen drift: nothing failed
 * when one of them changed on one side only. The one shape that differs is alpha —
 * the canvas carries it as a separate `strokeOpacity` attribute, while the print
 * server wants it inside the colour — so the sub-area stroke is COMPOSED from the two
 * shared values instead of being written out again with the byte inlined.
 */
const withAlpha = (hex: string, opacity: number): string =>
  hex +
  Math.round(Math.max(0, Math.min(1, opacity)) * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase()

/** The app's sub-area magenta at the app's own alpha. The canvas draws a sub-area as
 *  one solid unfilled stroke, so the printed sheet does too - a fill here would print
 *  over terrain the preview leaves visible. */
const PRINT_SUBAREA_STROKE = withAlpha(SUBAREA_STROKE, SUBAREA_STROKE_OPACITY)

// ── Wire shapes ───────────────────────────────────────────────────────────

/** A coordinate pair in the server's order: longitude first. */
export type LonLat = [number, number]

export interface MapPrintMargins {
  top: number
  bottom: number
  left: number
  right: number
}

/**
 * One ring that reads as an outer with its holes punched out: the outer up to the
 * bridge vertex, the hole traversed from its own nearest vertex and closed, back over
 * the bridge, then the rest of the outer. The slit has zero width, so it prints as a
 * hairline rather than a gap.
 *
 * The bridge is the closest vertex pair. That is not provably inside the polygon for an
 * arbitrarily concave outer, but it is for the shapes this draws - a lake inside a
 * parcel - and the failure mode if it ever is not is a thin sliver, against a current
 * failure mode of marking open water as huntable.
 *
 * Cost is |outer| x |hole| per hole. This runs once per print, not per frame.
 */
function keyholeRing(outer: readonly GeoPt[], holes: readonly GeoPt[][]): GeoPt[] {
  let ring: GeoPt[] = [...outer]
  for (const hole of holes) {
    if (hole.length < 3) continue
    let best = Infinity
    let ri = 0
    let hi = 0
    for (let i = 0; i < ring.length; i++) {
      for (let j = 0; j < hole.length; j++) {
        const d = (ring[i].lat - hole[j].lat) ** 2 + (ring[i].lon - hole[j].lon) ** 2
        if (d < best) {
          best = d
          ri = i
          hi = j
        }
      }
    }
    const traversed = [...hole.slice(hi), ...hole.slice(0, hi), hole[hi]]
    ring = [...ring.slice(0, ri + 1), ...traversed, ring[ri], ...ring.slice(ri + 1)]
  }
  return ring
}

export interface MapPrintFeature {
  type: 'symbol' | 'line' | 'polygon'
  /**
   * The coordinates themselves, or — for a `line` — the NAME of an entry in the
   * request's `geometries` map, which the server dereferences before drawing.
   */
  geometry: LonLat | LonLat[] | LonLat[][] | string
  color?: string
  /** symbol */
  symbol?: string
  height?: number
  label?: string
  label_height?: number
  label_color?: string
  /** line */
  width?: number
  /**
   * Which side of a CLOSED path the stroke hugs; the omitted default centres it.
   * Resolved against the whole geometry's INTERIOR, not against a single ring and
   * not against ring direction — so on a polygon with holes, one `outside` puts the
   * stroke beyond the outer rings and inside the holes, a hole not being interior.
   * Give it the polygon, not the rings, and the sides come out right on their own.
   */
  position?: 'center' | 'inside' | 'outside'
  end_cap?: 'butt' | 'square' | 'round' | 'arrow'
  dash?: number[]
  miter_limit?: number
}

/**
 * The base-map flavours the print server renders — one of these four keywords is
 * what `base_map` takes, never a catalogue id.
 */
export type MapPrintBaseMap = 'basic' | 'fancy' | 'orto' | 'hybrid'

const PRINT_BASE_MAPS: readonly MapPrintBaseMap[] = ['basic', 'fancy', 'orto', 'hybrid']

/**
 * The flavour named by a catalogue base-map id, defaulting to `basic`.
 *
 * The catalogue does NOT identify itself by flavour keyword. Its ids are
 * `tanger-world/<flavour>-<mask>`, where the mask encodes the account's own
 * feature bits and so differs per account and per environment — a real one looks
 * like `tanger-world/orto-A3f`. The four buttons in the layer picker are exactly
 * these four flavours, in the catalogue's order, so the keyword is the leading
 * word of the id's last path segment.
 *
 * Matching the id against the keywords whole was the first attempt and silently
 * printed `basic` for every pick, because no id is ever equal to a keyword.
 *
 * An id that names no flavour is clamped to `basic` rather than forwarded: the
 * job is validated strictly, so a flavour the server has no name for would fail
 * the WHOLE print, and losing the shading is a smaller loss than losing the
 * sheet. Null — the catalogue has not answered yet — takes the same default.
 */
export function printBaseMap(id: string | null | undefined): MapPrintBaseMap {
  if (!id) return 'basic'
  const leaf = id.slice(id.lastIndexOf('/') + 1)
  const keyword = leaf.split('-')[0]
  return PRINT_BASE_MAPS.find((flavour) => flavour === keyword) ?? 'basic'
}

export interface MapPrintRequest {
  center: LonLat
  /**
   * 1:N. Bounded by what the server actually renders — see
   * `mapPrintLimits.ts`, whose range is narrower than the published
   * documentation's.
   */
  scale: number
  page_width: number
  page_height: number
  margins: MapPrintMargins
  base_map: MapPrintBaseMap
  property_borders: boolean
  features?: MapPrintFeature[]
  /**
   * Geometries more than one feature draws, by the name those features carry in
   * place of the coordinates. A polygon (rings) is as shareable as a single ring —
   * the server resolves the name to a geometry, not to a ring.
   */
  geometries?: Record<string, LonLat[] | LonLat[][]>
  symbols?: Record<string, string>
}

export interface BuildMapPrintRequestOptions {
  map: TeamMap
  center: GeoPt
  scale: number
  format: PaperFormat
  orientation: PrintOrientation
  marginMm?: number
  /**
   * Explicit page millimetres, which win over `format`/`orientation` — see
   * `PrintSheetSpec`. Named `customPage` rather than `page`: `page` is a
   * local inside `buildMapPrintRequest` (the resolved wire dimensions) and
   * a same-named option would shadow it.
   */
  customPage?: { w: number; h: number }
  propertyBorders?: boolean
  /** Raw pin artwork by pin type; sanitised here before it is shipped. */
  symbolSvgs: Record<string, string>
  /**
   * The WITH-background drawing of each type in `symbolSvgs`, which is what a
   * pin's stored colour is compared against to tell an override from the type's
   * own colour (see `pinColourOverride`). Only needed when `symbolSvgs` holds the
   * bare drawings; it defaults to `symbolSvgs` itself.
   */
  symbolDesignSvgs?: Record<string, string>
  /**
   * Printed pin and label size, from the print panel's sliders. Defaults to
   * `defaultPrintPinSizes(appearance)` — what the sliders start at.
   */
  pinSizes?: PrintPinSizes
  /**
   * The whole appearance object, not the two or three scalars off it that this used
   * to take.
   *
   * Required, and one option rather than several, because every setting it carries is
   * a term in what the sheet should look like — and a caller that forgets one silently
   * prints something the preview does not show. Passing the object makes adding a
   * setting a compile-time decision here instead of a gap nobody notices.
   *
   * Not everything on it is forwarded, and the ones that are not are named in the test
   * that partitions them, each with its reason.
   */
  appearance: MapAppearance
  /**
   * The base map the surface is DRAWING, as its catalogue id — `null` before the
   * catalogue has answered.
   *
   * Required rather than optional, and for the same reason `appearance` is one
   * object instead of the scalars off it: an omitted setting does not fail, it
   * silently prints something the preview does not show. That is exactly how the
   * sheet came back on the default terrain map while the screen showed the
   * aerial one.
   */
  baseMapId: string | null
  /**
   * Pins and lines hidden in the pin list, by id — kept off the sheet exactly as
   * the map keeps them off the screen, which is how a team prints only the stands
   * in use that day.
   *
   * Required for the same reason `baseMapId` is: both print surfaces once left it
   * out, and the sheet printed every pin the map on screen had hidden.
   */
  hiddenObjectIds: ReadonlySet<string>
  activeOverlays: readonly PrintOverlay[]
  capabilities: readonly string[]
  printableFlavors: readonly string[]
  /** The loaded projector used by the visible print frame. Submission waits for it. */
  sheet: PrintProjector
}

export interface BuildMapPrintRequestResult {
  request: MapPrintRequest
  /**
   * Ids of pins that could not be drawn because no artwork was available for
   * their type. Reported rather than dropped quietly: a print that silently
   * loses stands is worse than one that tells the user which are missing.
   */
  omittedPins: string[]
  skips: PrintSkip[]
  pointCount: number
}

// ── Colour ────────────────────────────────────────────────────────────────

const hex2 = (n: number): string =>
  Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')

/**
 * A web colour in the print server's form (`#rrggbb` or `#aarrggbb`).
 *
 * Accepts the three shapes the canvas actually produces: `#rgb`, `#rrggbb`,
 * `#rrggbbaa` and `rgba()`. Anything else becomes opaque black — a visible
 * wrong colour beats a rejected print job, and the fallback is unreachable for
 * colours that came out of the app's own pickers.
 */
export function toTangerColor(color: string | undefined): string {
  const value = (color ?? '').trim().toLowerCase()

  const rgbaMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(value)
  if (rgbaMatch) {
    const [, r, g, b, a] = rgbaMatch
    const rgb = `${hex2(Number(r))}${hex2(Number(g))}${hex2(Number(b))}`
    if (a === undefined || Number(a) >= 1) return `#${rgb}`
    return `#${hex2(Number(a) * 255)}${rgb}`
  }

  const hex = /^#([0-9a-f]{3,8})$/.exec(value)?.[1]
  if (hex === undefined) return '#000000'

  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }
  if (hex.length === 6) return `#${hex}`
  if (hex.length === 8) {
    // CSS puts alpha last; the print server puts it first.
    return `#${hex.slice(6, 8)}${hex.slice(0, 6)}`
  }
  return '#000000'
}

// ── Geometry ──────────────────────────────────────────────────────────────

/**
 * A usable pin scale, defaulting to 1.
 *
 * `clampAppearance` already bounds the stored setting to 0.5–2.0, but this
 * function is exported and the value originates in localStorage, so a zero or a
 * NaN reaching here would emit a symbol the server renders at no height at all —
 * pins silently missing from the PDF. Falling back to the baseline keeps them on
 * the paper at the wrong size, which is a visible problem rather than an
 * invisible one.
 */
function usablePinScale(scale: number | undefined): number {
  if (scale === undefined || !Number.isFinite(scale) || scale <= 0) return 1
  return scale
}

/** A usable printed pin height, falling back to the default for the same reason. */
function usablePinMm(mm: number, appearance: MapAppearance): number {
  if (Number.isFinite(mm) && mm > 0) return mm
  return PRINT_SYMBOL_HEIGHT_MM * usablePinScale(appearance.pinScale)
}

/**
 * A coordinate pair in the server's order, longitude first, rounded to the six
 * decimals the wire carries.
 *
 * The rounding lives here rather than at each call site because this is the one
 * door every coordinate leaves through — the centre, every ring, every drawn
 * line, every pin — so nothing can ship full precision by being forgotten. It is
 * also the LAST step by construction: clipping and simplification run on the
 * projected metres upstream of it, so no geometry maths ever sees a rounded
 * input and accumulates the error twice.
 */
const toLonLat = (p: GeoPt): LonLat => [roundCoord(p.lon), roundCoord(p.lat)]

/** A polygon ring in the server's form: longitude first, first point repeated. */
function closedRing(polygon: readonly GeoPt[]): LonLat[] {
  const ring = polygon.map(toLonLat)
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]])
  return ring
}

/**
 * Does this wire polyline carry two points the wire can tell apart?
 *
 * Asked AFTER rounding, because that is where the answer can change: a clipped
 * piece a few centimetres long has real length in the projector's metres and
 * none at all once its ends round to the same 6-decimal coordinate. A
 * zero-length `line` is a stroke with no direction — a stray dot at best, a
 * rejected job at worst — and it carries nothing a reader could have seen.
 */
function hasDistinctPoints(piece: readonly LonLat[]): boolean {
  const first = piece[0]
  if (first === undefined) return false
  return piece.some(([lon, lat]) => lon !== first[0] || lat !== first[1])
}

// ── Build ─────────────────────────────────────────────────────────────────

export function buildMapPrintRequest({
  map,
  center,
  scale,
  format,
  orientation,
  marginMm = DEFAULT_PRINT_MARGIN_MM,
  customPage,
  propertyBorders = false,
  symbolSvgs,
  symbolDesignSvgs = symbolSvgs,
  pinSizes,
  appearance,
  baseMapId,
  hiddenObjectIds,
  activeOverlays,
  capabilities,
  printableFlavors,
  sheet,
}: BuildMapPrintRequestOptions): BuildMapPrintRequestResult {
  // Pin and label size are the print panel's own, seeded from the Pin size
  // setting and the pin-names switch. On screen the label size tracks zoom
  // (pinLabelCssPx: 24 CSS px zoomed in, down to an 8 px floor zoomed out); print
  // has no screen zoom to track — the sheet is composed at a fixed paper scale —
  // so it prints one stable label size rather than mirroring a transient zoom.
  const sizes = pinSizes ?? defaultPrintPinSizes(appearance)
  const pinMm = usablePinMm(sizes.pinMm, appearance)
  const labelMm = Number.isFinite(sizes.labelMm) && sizes.labelMm > 0 ? sizes.labelMm : 0
  const showLabels = labelMm > 0
  // The layer switches reach the sheet: print follows the screen it was composed
  // on, so a layer hidden in the preview is left off the PDF too.
  const showSubAreas = appearance.subAreasVisible
  const showForbidden = appearance.forbiddenAreasVisible
  // CLUB-667: pin types the user has hidden. Applied inside this builder so a
  // hidden pin can never reach `omittedPins` — that list is reported as artwork
  // that *failed* to draw, and a pin the user deliberately hid is not a failure
  // to warn about.
  const pinVisible = pinTypeVisibility(appearance.hiddenPinTypes)
  const features: MapPrintFeature[] = []
  const symbols: Record<string, string> = {}
  const omittedPins: string[] = []

  /**
   * Geometries drawn by more than one feature, by the name those features carry
   * instead of the coordinates. The server resolves a string `geometry` against
   * this map, so a shape used twice is serialised once.
   *
   * The band and the core of a ground ring are the one guaranteed duplicate in
   * this payload — they trace exactly the same line by construction, which is
   * why the pipeline resolves each ring once — and they are also the largest
   * thing in it. Everything else is registered only if it, too, ends up drawn
   * twice: a geometry with a single feature stays inline, because naming it
   * would add a map entry and remove nothing.
   */
  const geometries: Record<string, LonLat[] | LonLat[][]> = {}
  let sharedGeometryCount = 0
  const shareGeometry = (geometry: LonLat[] | LonLat[][]): string => {
    const name = `g${sharedGeometryCount++}`
    geometries[name] = geometry
    return name
  }

  /**
   * A ground stroke's width on paper, from the shared law.
   *
   * The latitude is the sheet's centre. The law's response to latitude is a (cos φ)^0.6
   * term, so across one sheet the variation is far below a printer dot; taking it per
   * feature would be precision the medium cannot render.
   */
  const strokeMm = (base: number, thicknessScale: number) =>
    groundStrokeMm(base, { scale, lat: center.lat, thicknessScale })

  const lt = appearance.lineThickness

  /**
   * The sheet's clip rectangle in the projector's metres, with everything the
   * pipeline below needs to get geometry into that plane and back out again.
   * Null when no projector was given, which switches clipping and simplification
   * off and leaves rounding as the only thing that happens to a coordinate.
   *
   * The margin is deliberately generous, for two reasons. A stroke is centred on
   * its ring, so a ring lying on the sheet edge still paints half its width
   * inward; cutting at the bare rectangle would end that stroke early and leave a
   * frayed edge the reader can see. And the server frames the sheet itself — same
   * centre, same scale, its own arithmetic — so a rectangle computed here to the
   * millimetre is not one to trust to the millimetre. Five percent of the sheet
   * plus the widest ground stroke covers both. `sheetRect` takes ONE margin for
   * both axes, so the larger of the two five-percent shares is used: erring wide
   * keeps a little more off-sheet geometry, which costs bytes and shows nothing,
   * where erring narrow would cut ground the reader should have seen.
   */
  const sheetGeometry = ((): {
    rect: Rect
    anchorRect: Rect
    forward: (p: GeoPt) => Pt2
    inverse: (xy: Pt2) => GeoPt
    tolerance: number
  } | null => {
    if (!sheet) return null
    // Same override as the wire `page_width`/`page_height` below: a custom
    // page's own millimetres, so the clip rectangle matches what the server
    // actually renders rather than the named format's table entry.
    const extent = groundExtentMetres({ scale, format, orientation, marginMm, page: customPage })
    const widestStrokeM = (strokeMm(GROUND_BASE_WIDTH.halo, lt.border) * scale) / 1000
    const margin = Math.max(extent.w, extent.h) * 0.05 + widestStrokeM
    // Every quantity above is ground metres; the rectangle and the tolerance
    // below live in the projector's plane. Unity for a national grid, and
    // 1/cos(lat) for the worldwide Mercator fallback — where clipping an
    // unconverted rectangle would cut away half the ground the sheet actually
    // prints at 60°N, leaving the paper's outer band empty.
    const k = sheet.crsMetresPerGroundMetre(center.lat)
    return {
      rect: sheetRect(sheet.forward(center), { w: extent.w * k, h: extent.h * k }, margin * k),
      anchorRect: sheetRect(sheet.forward(center), { w: extent.w * k, h: extent.h * k }, 0),
      forward: (p) => sheet.forward(p),
      inverse: (xy) => sheet.inverse(xy),
      tolerance: printPixelToleranceMetres(scale) * k,
    }
  })()

  const withinSheet = ([x, y]: Pt2, rect: Rect): boolean =>
    x >= rect.minX && x <= rect.maxX && y >= rect.minY && y <= rect.maxY

  /**
   * A stroked ring as the wire geometries that survive the sheet: the closed ring
   * itself when the whole of it fits, or one open polyline per piece that crosses
   * the sheet when it does not. A ring that misses the sheet entirely yields
   * nothing, which is the bulk of the saving — on the request that prompted this,
   * 94% of the coordinates were outside the paper.
   *
   * A clipped piece is NEVER re-closed. Its ends are cuts at the sheet edge, not a
   * join, and closing one would draw a stroke straight across the sheet between
   * two unrelated crossings. That is also why the whole-ring case is decided up
   * front by testing the vertices, rather than inferred from the clip result: the
   * difference between the two is a ring closing and a ring slicing the map in
   * half, and it is too load-bearing to leave to a length comparison.
   */
  const strokeGeometries = (ring: readonly GeoPt[]): LonLat[][] => {
    if (ring.length < 2) return []
    if (!sheetGeometry) return [closedRing(ring)]
    const { rect, forward, inverse, tolerance } = sheetGeometry
    const projected = ring.map(forward)
    if (projected.every((p) => withinSheet(p, rect))) {
      // Judged by the same rule as a clipped piece, and for the same reason: a
      // ring smaller than the ~11 cm the wire can express rounds to a single
      // repeated coordinate, and a closed `line` through one point is a stroke
      // with no direction — a stray dot at best, a rejected job at worst. The
      // fast path skips the clipper, not this check.
      const whole = closedRing(simplifyPolyline(projected, tolerance, true).map(inverse))
      return hasDistinctPoints(whole) ? [whole] : []
    }
    return clipPolylineToRect(projected, rect, true)
      .map((piece) => simplifyPolyline(piece, tolerance, false).map((p) => toLonLat(inverse(p))))
      .filter(hasDistinctPoints)
  }

  /**
   * A fill polygon — outer ring first, then its holes — as the keyholed wire rings
   * that survive the sheet. Clipping can split one polygon into several disjoint
   * pieces, so one polygon in can be several fills out.
   *
   * The keyhole is cut AFTER clipping and simplification, never before. Its slit is
   * zero-width: given to the clipper it is degenerate geometry the intersection can
   * tear along, and given to Douglas-Peucker the slit's two coincident traversals
   * are exactly what its perpendicular-distance test cannot rank. Both want the
   * honest outer-and-holes shape; the slit is a rendering trick for a wire that
   * carries one ring, and it belongs last.
   *
   * A polygon that fits on the sheet skips the clipper altogether, so an unclipped
   * shape ships as the builder has always shipped it rather than as the clipping
   * library's renormalised rewriting of it.
   */
  const fillGeometries = (poly: readonly GeoPt[][]): LonLat[][] => {
    const outer = poly[0]
    if (outer === undefined || outer.length < 3) return []
    if (!sheetGeometry) return [closedRing(keyholeRing(outer, poly.slice(1)))]
    const { rect, forward, inverse, tolerance } = sheetGeometry
    const projected = poly.map((ring) => ring.map(forward))
    const pieces = projected.every((ring) => ring.every((p) => withinSheet(p, rect)))
      ? [projected]
      : clipPolygonToRect(projected, rect)
    return pieces.flatMap((piece) => {
      const [outerRing, ...holes] = piece
      if (outerRing === undefined) return []
      const keptOuter = simplifyPolyline(outerRing, tolerance, true).map(inverse)
      // The OUTER is what makes a piece a fill, so it is judged on its own and the
      // whole piece goes with it. Filtering the rings as one list instead would
      // promote a hole into first position when an outer fell away, and the keyhole
      // would then paint the exclusion as the fill — the exact failure the keyhole
      // exists to prevent, and one that shows up as wrongly washed ground rather
      // than as an error.
      if (keptOuter.length < 3) return []
      const keptHoles = holes
        .map((ring) => simplifyPolyline(ring, tolerance, true).map(inverse))
        .filter((ring) => ring.length >= 3)
      return [closedRing(keyholeRing(keptOuter, keptHoles))]
    })
  }

  /**
   * A ground polygon — outer ring first, then its holes — as the CLOSED wire rings
   * that survive the sheet. Honest outer-and-holes, never keyholed.
   *
   * Clipped as a POLYGON, which is what it is. A polyline clip cuts a ring that
   * leaves the sheet into open fragments, and the server's `position` acts on closed
   * paths alone, so every ground larger than its paper would quietly fall back to a
   * centred band — the shapes most likely to be printed losing the treatment first.
   * A polygon clip closes each surviving piece along the clip rectangle instead.
   *
   * The synthetic edges that closure introduces ARE drawn, and are harmless by
   * construction: the rectangle stands a full 5% of the sheet plus the widest ground
   * stroke beyond the printed area (`sheetGeometry`), so the core centred on such an
   * edge, and the band hugging its far side, are both off the page. Shrinking that
   * margin would bring a spurious border along the paper's edge into view, which is
   * the one thing to keep in mind if it is ever retuned.
   *
   * Not keyholed, unlike `fillGeometries`: that slit is a trick for painting a hole
   * through a single ring, and a STROKE would draw the slit — and the server would
   * read the interior through it, which is exactly the question `position` asks.
   */
  const outlineGeometries = (poly: readonly GeoPt[][]): LonLat[][][] => {
    const outer = poly[0]
    if (outer === undefined || outer.length < 3) return []
    // The outer is judged on its own and the whole piece goes with it, the same rule
    // `fillGeometries` states: filtering the rings as one list would promote a hole
    // into first position when an outer fell away.
    const assemble = (rings: GeoPt[][]): LonLat[][][] => {
      const [first, ...rest] = rings
      if (first === undefined) return []
      const outerWire = closedRing(first)
      if (!hasDistinctPoints(outerWire)) return []
      return [[outerWire, ...rest.map(closedRing).filter(hasDistinctPoints)]]
    }
    if (!sheetGeometry) return assemble(poly.map((ring) => [...ring]))
    const { rect, forward, inverse, tolerance } = sheetGeometry
    const projected = poly.map((ring) => ring.map(forward))
    const pieces = projected.every((ring) => ring.every((p) => withinSheet(p, rect)))
      ? [projected]
      : clipPolygonToRect(projected, rect)
    return pieces.flatMap((piece) => {
      const [outerRing, ...holes] = piece
      if (outerRing === undefined) return []
      const keptOuter = simplifyPolyline(outerRing, tolerance, true).map(inverse)
      if (keptOuter.length < 3) return []
      const keptHoles = holes
        .map((ring) => simplifyPolyline(ring, tolerance, true).map(inverse))
        .filter((ring) => ring.length >= 3)
      return assemble([keptOuter, ...keptHoles])
    })
  }

  const geo: RingGeometry = { stroke: strokeGeometries, fill: fillGeometries }

  // The boundary, through the SAME shape resolver the read-only surfaces use, so a
  // property group prints as the one unioned outline the preview shows rather than as
  // one line per stored parcel with every internal seam at full weight and each
  // parcel's halo stacked along it.
  const boundaryShapes = groundShapes(map.boundaryAreas, 'hunt')

  // The band is the app's standard halo, withheld at low zoom, or the thin band it
  // draws over a wash, which is never withheld. Evaluated at the zoom this sheet is
  // equivalent to, the same two conditions the screen applies.
  const printZoom = groundEquivalentPrintZoom({ scale, lat: center.lat })
  const style = appearance.borderAppearance
  const bandOnPaper = style !== 'standard' || shouldDrawGroundHalo(printZoom)
  const outline = huntOutlineWidths(style)

  // The wash, before every ground feature so the outlines land on top of it. The
  // same geometry the screen draws: the reverse ovals with the grounds cut out, or
  // the hunt union minus the forbidden areas — minus only the shown ones, so a
  // hidden forbidden area leaves no hole in the green, as on screen. Each polygon
  // is keyholed because the wire's polygon feature carries one ring.
  const washPolys =
    style === 'reverse'
      ? reverseWashPolygons(map.boundaryAreas ?? [])
      : style === 'filled'
        ? filledWashPolygons(map.boundaryAreas ?? [], showForbidden ? (map.forbiddenPolygons ?? []) : [])
        : []
  for (const poly of washPolys) {
    for (const geometry of geo.fill(poly)) {
      features.push({
        type: 'polygon',
        geometry,
        color: toTangerColor(style === 'reverse' ? REVERSE_WASH_FILL : FILLED_WASH_FILL),
      })
    }
  }

  /**
   * The polygons to FILL for a unioned shape — each an outer ring followed by its
   * holes — on the two layers that have a fill.
   *
   * The print wire's polygon feature carries ONE ring, so filling a unioned outer
   * paints its holes back IN. Unioning the layers is what creates such holes in the
   * first place: four forbidden areas framing a courtyard merge into one outer plus
   * that courtyard, and the fill then marks legal ground as no-hunting on a sheet a
   * hunter carries into the field.
   *
   * A hole is punched with a KEYHOLE rather than by falling back to member outers.
   * The member fallback was only ever a way of avoiding holes, and it stopped being
   * adequate once a hole reliably survives into the resolved shape: a single record
   * carrying its own lake has no smaller unit to fall back to, so the sheet filled the
   * water in while the screen punched it out - a divergence on the one artefact a
   * hunter carries into the field.
   *
   * A keyhole is the outer ring, a zero-width slit to the hole, the hole traversed, and
   * back. It renders as an annulus under BOTH fill rules, which is what makes it the
   * right primitive here: this payload goes to an external renderer, and whether its
   * `LonLat[][]` geometry means outer-plus-holes or a multipolygon is undocumented and
   * unverified. A keyhole needs no answer to that question and no schema change.
   *
   * Under a non-zero rule the annulus depends on the hole running opposite its outer,
   * which is exactly the invariant `unionedRings` now establishes, so the hole is
   * traversed in its stored order.
   *
   * The keyhole itself is cut downstream, by `fillGeometries`, because the clip and
   * the simplification both need the outer and its holes still separate.
   */
  const fillPolygons = (shape: GroundShape): GeoPt[][][] => {
    const outers = shape.rings.slice(0, shape.outerCount)
    if (shape.rings.length === shape.outerCount) return outers.map((outer) => [outer])
    // `ringsToPolygons` pairs each outer with the holes that are actually INSIDE it, by
    // containment. Re-deriving that pairing here by nearest-vertex distance got it wrong:
    // distance was measured in raw lat/lon degrees, where a degree of longitude is about
    // half a degree of latitude at 60 north, so a lake near a shared seam could measure
    // closest to the NEIGHBOURING parcel and be keyholed out of it - notching ground that
    // has no hole while the real lake stayed filled. It also threw away an association
    // the resolver had already computed correctly.
    return ringsToPolygons(shape.rings, shape.outerCount)
  }

  // Features follow the screen's layer stack; no halo may cover another core.
  const halos: MapPrintFeature[] = [], cores: MapPrintFeature[] = []
  for (const shape of boundaryShapes) {
    // The same precedence and the same sanitiser as every screen: the team's colour,
    // then the area's own, then the domain default. Reading them in the other order
    // printed a per-ground colour the preview does not draw, and reading them raw let
    // an unparseable stored value through to the server.
    const css = resolveHuntBorderColor({ areaColor: shape.color, teamColor: map.borderColor })
    const color = toTangerColor(css)

    // Each part's printed geometry, resolved once and drawn by both layers. The band
    // and the core follow exactly the same path by construction, and projecting,
    // clipping and simplifying it a second time to draw it again is the most
    // expensive work in this builder done for nothing.
    //
    // One geometry per PART — outer ring and its holes together — rather than one per
    // ring. The server resolves `position` against the polygon's interior, and a hole
    // is not interior, so a single `outside` puts the band beyond the outer edges and
    // into the holes on its own. Handing it the rings separately would move that
    // classification into this builder, which is both redundant and a thing a client
    // can get wrong.
    const wirePolygons = fillPolygons(shape).flatMap(outlineGeometries)

    // When the band is drawn, every part carries two features, so each is registered
    // under a name both carry and the coordinates travel once. When it is withheld the
    // core is the only stroke there is, and the parts stay inline.
    const wire: (LonLat[][] | string)[] = bandOnPaper
      ? wirePolygons.map(shareGeometry)
      : wirePolygons

    // The band, before the core, so the core lands on top of it.
    //
    // On every ring, holes included: the MapLibre screen draws it that way, offset
    // into the hole (away from the ground), and so does the app
    // (`StrokePosition.outside` on the whole path).
    //
    // `position` replaces a centred stroke and fixes two things at once. A centred
    // band of width `band` reached only `band / 2` beyond the edge where the screen
    // reaches `band`, and it laid the other half of itself ON the ground. Clipped to
    // one side the server keeps the full width there, so the sheet has the screen's
    // extent and no inward spill.
    if (bandOnPaper) {
      const haloColor = toTangerColor(withAlpha(css, GROUND_HALO_OPACITY))
      for (const geometry of wire) {
        halos.push({
          type: 'line',
          geometry,
          color: haloColor,
          width: strokeMm(outline.band, lt.border),
          position: 'outside',
        })
      }
    }

    // The edge itself, centred on the boundary: a hole's edge is drawn exactly like
    // the ground's, which is what the screen does too.
    for (const geometry of wire) {
      cores.push({
        type: 'line',
        geometry,
        color,
        width: strokeMm(outline.core, lt.border),
      })
    }
  }

  // A forbidden area hidden by its switch leaves the sheet as it leaves the
  // screen, fill and edge alike.
  const forbiddenShapes = showForbidden ? groundShapes(map.forbiddenPolygons, 'forbidden') : []
  for (const shape of forbiddenShapes) {
    for (const poly of fillPolygons(shape)) {
      for (const geometry of geo.fill(poly)) features.push({ type: 'polygon', geometry, color: toTangerColor(FORBIDDEN_FILL) })
    }
  }
  for (const shape of forbiddenShapes) pushAreaOutline(features, geo, shape, FORBIDDEN_STROKE, strokeMm(GROUND_BASE_WIDTH.forbidden, lt.forbidden))
  if (showSubAreas) {
    for (const shape of groundShapes(map.subAreaPolygons, 'subarea')) pushAreaOutline(features, geo, shape, PRINT_SUBAREA_STROKE, strokeMm(GROUND_BASE_WIDTH.subarea, lt.subarea))
  }
  features.push(...halos, ...cores)

  // Hidden in the pin list, or its kind switched off (arrows, lines): off the
  // sheet as it is off the screen.
  const drawnLines = (map.lines ?? []).filter((line) => !hiddenObjectIds.has(line.id) && isLineKindVisible(appearance, line.kind) && line.points.length >= 2 && hasDistinctPoints(line.points.map(toLonLat)))
  for (const style of ['solid', 'dashed', 'dotted', 'dash_dot'] as const) {
    const group = drawnLines.filter((line) => (line.style ?? 'solid') === style).flatMap((line) => {
      const width = lineStrokeMm(line.width, scale, center.lat, line.kind === 'arrow' ? lt.arrows : lt.lines)
      const { rect, forward, inverse, tolerance } = sheetGeometry!
      const pieces = clipPolylineToRect(line.points.map(forward), rect, false)
        .map((piece) => simplifyPolyline(piece, tolerance, false).map((p) => toLonLat(inverse(p))))
        .filter(hasDistinctPoints)
      return pieces.map((piece) => ({ line, width, geometry: shareGeometry(piece) }))
    })
    for (const { geometry, width } of group) {
      const dash = lineDashPattern(style, LINE_BORDER_FACTOR)
      features.push({ type: 'line', geometry, width: width * LINE_BORDER_FACTOR, color: '#000000', ...(dash ? { dash } : {}) })
    }
    for (const { line, geometry, width } of group) {
      const dash = lineDashPattern(style)
      features.push({ type: 'line', geometry, width, color: toTangerColor(line.color ?? LINE_DEFAULT_COLOR), ...(dash ? { dash } : {}) })
    }
  }
  // DOM arrowheads sit above all line layers. An off-sheet anchor stays off paper.
  for (const line of drawnLines.filter((line) => line.kind === 'arrow')) {
    const end = line.points.at(-1)!
    if (!withinSheet(sheet.forward(end), sheetGeometry!.anchorRect)) continue
    const width = lineStrokeMm(line.width, scale, center.lat, lt.arrows)
    const head = printArrowHead(line.points, width, scale, center.lat, sheet)
    if (!head) continue
    const geometry = shareGeometry(closedRing(head))
    features.push({ type: 'polygon', geometry, color: toTangerColor(line.color ?? LINE_DEFAULT_COLOR) })
    features.push({ type: 'line', geometry, color: '#000000', width: 1 / PAGE_PX_PER_MM })
  }

  for (const object of map.objects ?? []) {
    // Before the artwork lookup, so a hidden pin — by type or by the pin list —
    // is never counted as omitted. Note the type test is on the object's own
    // type, while an artwork failure falls back to the generic `pin` artwork:
    // hiding `pin` hides objects whose type is literally `pin`, not every pin
    // drawn with that glyph.
    if (!pinVisible(object.type) || hiddenObjectIds.has(object.id)) continue
    // A pin off the sheet cannot be printed, so it is dropped here — before the
    // artwork lookup, so it neither reaches `omittedPins` (off the paper is not a
    // drawing failure to warn about) nor drags its SVG into a payload nothing
    // references. Its own point is the test: unlike a ring, a symbol has nothing
    // to clip, it is either on the paper or it is not.
    if (sheetGeometry && !withinSheet(sheetGeometry.forward(object), sheetGeometry.anchorRect)) continue
    // Bare custom pins consist entirely of a glyph; old workers cannot draw it.
    if (object.type === 'custom' && !appearance.pinBackground) { omittedPins.push(object.id); continue }
    // The server ignores a symbol's `color` (only a `color_filter` matrix
    // reaches the paint, and no single matrix reproduces the screen's two-tone
    // recolour), so the colour is painted into the artwork itself, exactly as the
    // map's sprites do it — one symbol entry per type and colour. The anchor
    // follows the artwork actually drawn: a custom pin printed with the generic
    // pin's drawing stands on that drawing's tip (`preparePrintArtwork`).
    const artwork = printPinArtwork(object, { appearance, symbolSvgs, symbolDesignSvgs }, pinMm)
    if (artwork === null) {
      omittedPins.push(object.id)
      continue
    }
    if (symbols[artwork.key] === undefined) symbols[artwork.key] = artwork.svg
    const label = showLabels ? object.label?.trim() : undefined
    features.push({
      type: 'symbol',
      geometry: toLonLat(object),
      symbol: artwork.key,
      // Twice the drawing's size for a pin standing on its tip, whose viewBox is
      // doubled to put the tip at the server's centre anchor.
      height: artwork.height,
      // Required by the server's schema for every feature, though a symbol's
      // paint never reads it.
      color: toTangerColor(object.color ?? '#ffffff'),
      ...(label
        ? { label, label_height: labelMm, label_color: PRINT_LABEL_COLOR }
        : {}),
    })
  }

  // An empty symbol carries text on older workers that have no text feature.
  // Names print as pin labels do — white in the server's outline, no pill — which
  // is how the print preview draws a label (printPinLabelStyle).
  const addName = (name: string, anchor: GeoPt | null, sizeMm: number) => {
    if (!anchor || !name.trim() || !withinSheet(sheet.forward(anchor), sheetGeometry!.anchorRect)) return
    const tinyHeight = 0.001
    const [x, y] = sheet.forward(anchor)
    const mmToCrs = scale / 1000 * sheet.crsMetresPerGroundMetre(center.lat)
    const carrier = sheet.inverse([x, y - (0.75 * sizeMm + tinyHeight / 2) * mmToCrs])
    symbols['__print_name__'] = '<svg viewBox="0 0 1 1"/>'
    features.push({ type: 'symbol', geometry: toLonLat(carrier), color: PRINT_LABEL_COLOR, symbol: '__print_name__', height: tinyHeight, label: name, label_height: sizeMm, label_color: PRINT_LABEL_COLOR })
  }
  // Line names ride the pin-label slider, as on screen they ride the pin-names
  // switch; sub-area names follow the sub-areas switch at the default size.
  if (showLabels) {
    for (const line of drawnLines) {
      if (!line.name?.trim()) continue
      const anchor = printLineNameAnchor(line, false, scale, center.lat, labelMm)
      addName(line.name, anchor, labelMm)
    }
  }
  if (showSubAreas) {
    for (const shape of groundShapes(map.subAreaPolygons, 'subarea')) {
      if (shape.name) addName(shape.name.toUpperCase(), polygonCentroidOfRings(shape.rings), PRINT_LABEL_MM)
    }
  }

  // The custom sheet's own millimetres when it has them; the paper table's
  // otherwise. This is what lands in `page_width`/`page_height` on the wire.
  const page = customPage ?? pageMm(format, orientation)

  const requestedBaseMap = printBaseMap(baseMapId)
  const knownBaseMap = !baseMapId || /^(basic|fancy|orto|hybrid)(-|$)/.test(baseMapId.slice(baseMapId.lastIndexOf('/') + 1))
  const baseMapSubstituted = !knownBaseMap || !printableFlavors.includes(requestedBaseMap)
  const baseMap = baseMapSubstituted ? 'basic' : requestedBaseMap
  if (capabilities.includes('miter_limit')) {
    for (const feature of features) if (feature.type === 'line') feature.miter_limit = 2
  }
  const countPoints = (geometry: unknown): number => {
    if (!Array.isArray(geometry)) return 0
    if (typeof geometry[0] === 'number') return 1
    return geometry.reduce((total, part) => total + countPoints(part), 0)
  }
  const pointCount = features.reduce((total, feature) => total + countPoints(typeof feature.geometry === 'string' ? geometries[feature.geometry] : feature.geometry), 0)
  const skips = collectPrintSkips({ map, appearance, hiddenObjectIds, symbolSvgs, symbolDesignSvgs, labelMm, activeOverlays, capabilities, propertyBorders, baseMapSubstituted, center, sheet, scale, format, orientation, customPage, marginMm })

  return {
    request: {
      center: toLonLat(center),
      scale,
      page_width: page.w,
      page_height: page.h,
      margins: { top: marginMm, bottom: marginMm, left: marginMm, right: marginMm },
      base_map: baseMap,
      property_borders: propertyBorders,
      // Omitted rather than empty: the server rejects unknown fields, and an
      // empty list is noise in a request that is deduplicated by its content.
      ...(features.length > 0 ? { features } : {}),
      // Omitted when nothing is shared, for the same reason `features` is: a
      // request with no duplicated geometry then looks exactly as it did before
      // names existed.
      ...(Object.keys(geometries).length > 0 ? { geometries } : {}),
      ...(Object.keys(symbols).length > 0 ? { symbols } : {}),
    },
    omittedPins,
    skips,
    pointCount,
  }
}

/**
 * The least a ring-carrying record has to have for the two push helpers below.
 *
 * Both a stored `GeoArea` and a resolved `GroundShape` satisfy it, which is the point:
 * the helpers are handed shapes now, and nothing they do needs an id or a name.
 */
type RingSet = { rings?: readonly GeoPt[][]; outerCount?: number }

/**
 * How a ring becomes wire geometry: zero or more geometries out, because the sheet
 * can cut one ring into several pieces or discard it entirely.
 *
 * Passed to the helpers below rather than reached for, because the pipeline closes
 * over the sheet's projector and rectangle — which only `buildMapPrintRequest` has —
 * while these helpers stay functions of their arguments alone.
 */
interface RingGeometry {
  stroke(ring: readonly GeoPt[]): LonLat[][]
  fill(poly: readonly GeoPt[][]): LonLat[][]
}

/**
 * An area's rings, the parts that enclose ground first and the parts that exclude it
 * after, dropping any ring too short to enclose anything.
 *
 * In stored coordinates, not wire ones: what a ring becomes on the wire is the sheet
 * pipeline's answer, and this function has no business pre-empting it.
 */
function strokedRings(a: RingSet): GeoPt[][] {
  const rings = a.rings ?? []
  const outerCount = a.outerCount ?? rings.length
  const usable = (r: GeoPt[]) => r.length >= 3
  return [
    ...rings.slice(0, outerCount).filter(usable),
    ...rings.slice(outerCount).filter(usable),
  ]
}

/**
 * An outline with no fill, for a layer the canvas strokes and does not fill.
 *
 * The area's own stored colour is deliberately NOT consulted. On screen this layer is
 * stroked in its fixed colour whatever the record says — `GroundShape.color` is read
 * by the hunt layer and ignored by the other two — so honouring it here would print a
 * colour the preview does not draw. That is the same print-versus-screen divergence
 * this whole change exists to remove, and it was live: a sub-area carrying an app-set
 * colour printed in that colour and previewed in magenta.
 */
function pushAreaOutline(
  features: MapPrintFeature[],
  geo: RingGeometry,
  a: RingSet,
  stroke: string,
  widthMm: number,
): void {
  for (const ring of strokedRings(a)) {
    for (const geometry of geo.stroke(ring)) {
      features.push({
        type: 'line',
        geometry,
        color: toTangerColor(stroke),
        width: widthMm,
      })
    }
  }
}
